import { DialogComponent } from '@theme/dialog';
import { StandardEvents } from '@shopify/events';

class LanoelRestock extends DialogComponent {
  #listeners;
  #request;
  #generation = 0;
  #data;
  #pendingSelection = false;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    this.#data = JSON.parse(this.querySelector('[data-restock-data]').textContent);
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#selectVariant, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
    this.#generation++;
    super.disconnectedCallback();
  }

  #selectVariant = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog')) return;
    const generation = ++this.#generation;
    this.#pendingSelection = true;
    this.#request?.abort();
    this.hidden = true;
    if (this.refs.dialog.open) await this.closeDialog();
    try {
      const { detail } = await event.promise;
      if (generation !== this.#generation || !this.isConnected) return;
      const variant = this.#data.variants.find(item => item.id === String(detail?.resource?.id));
      this.dataset.variantId = variant?.id || '';
      this.querySelector('[data-restock-variant]').textContent = variant && !this.#data.defaultVariant ? ` · ${variant.title}` : '';
      this.querySelector('[data-restock-status]').textContent = '';
      this.querySelector('[data-restock-form]').hidden = false;
      this.hidden = !variant || detail.resource.available !== false;
    } catch {
      if (generation === this.#generation) this.hidden = true;
    } finally {
      if (generation === this.#generation) this.#pendingSelection = false;
    }
  };

  showDialog() {
    if (this.hidden || this.#pendingSelection || !this.dataset.variantId) return;
    super.showDialog();
  }

  async subscribe(event) {
    event.preventDefault();
    event.stopPropagation();
    const form = this.querySelector('[data-restock-form]');
    if (!form.reportValidity() || this.#request || this.#pendingSelection || this.hidden) return;
    const variantId = this.dataset.variantId;
    const email = form.elements.email.value.trim();
    const button = form.querySelector('[data-restock-submit]');
    const status = this.querySelector('[data-restock-status]');
    const controller = new AbortController();
    this.#request = controller;
    const timeout = setTimeout(() => controller.abort(), 15000);
    button.disabled = true;
    button.textContent = this.#data.messages.sending;
    form.setAttribute('aria-busy', 'true');
    status.textContent = '';
    try {
      const response = await fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2026-10-15' },
        credentials: 'omit',
        signal: controller.signal,
        body: JSON.stringify({ data: {
          type: 'back-in-stock-subscription',
          attributes: { profile: { data: { type: 'profile', attributes: { email } } }, channels: ['EMAIL'] },
          relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } }
        } })
      });
      if (variantId !== this.dataset.variantId || !this.isConnected) return;
      if (response.status === 202) {
        form.hidden = true;
        form.reset();
        status.textContent = this.#data.messages.success;
      } else {
        status.textContent = response.status === 429 ? this.#data.messages.rate_limit : this.#data.messages.error;
      }
    } catch {
      if (this.isConnected && variantId === this.dataset.variantId) status.textContent = this.#data.messages.error;
    } finally {
      clearTimeout(timeout);
      if (this.#request === controller) this.#request = undefined;
      button.disabled = false;
      button.textContent = this.#data.messages.notify;
      form.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('lanoel-restock')) customElements.define('lanoel-restock', LanoelRestock);
